import gsap, { CustomEase } from '@lib/gsap'
import { onView, onDestroy } from './_'
import { prepLineDraw, playLineDraw, cleanLineDraw } from '@utils/line-draw'

export class StepAccordion {
  constructor(wrapper, options = {}) {
    this.wrapper = wrapper
    this.currentIndex = 0
    this.autoPlayDuration = options.duration || 10

    // Animation parameters
    this.ease = CustomEase.create('custom', '0.19, 1.00, 0.22, 1.00')
    this.easeDuration = 0.75

    this.init()
  }

  init() {
    this.#setupDom()
    this.#setupLayout()
    this.#playStep()
    this.#handleClick()
  }

  #setupDom() {
    this.dropdowns = gsap.utils.toArray('.ft-cmx-dropdown', this.wrapper)
    this.visuals = gsap.utils.toArray('.ft-cmx-visual-inner', this.wrapper)
    this.progresslines = gsap.utils.toArray('.ft-cmx-progress-line', this.wrapper)
  }

  #setupLayout() {
    this.dropdowns.forEach((dropdown) => {
      const line = dropdown.querySelector('[data-step-accordion-line]')
      const clip = dropdown.querySelector('[data-step-accordion-clip]')
      gsap.set(line, { scaleX: 0 })
      gsap.set(clip, { height: 0 })
    })
    gsap.set(this.progresslines, { scaleX: 0 })
    gsap.set(this.visuals, { autoAlpha: 0 })
    gsap.set(this.visuals[this.currentIndex], { autoAlpha: 1 })
  }

  #startAutoplay(index) {
    gsap.to(this.progresslines[index], {
      scaleX: 1,
      ease: 'none',
      duration: this.autoPlayDuration,
      onComplete: () => {
        this.#closeStep(this.currentIndex)

        if (this.currentIndex === this.dropdowns.length - 1) {
          this.currentIndex = 0
          gsap.set(this.progresslines, { scaleX: 0 })
        } else {
          this.currentIndex++
        }
        this.#playStep(this.currentIndex)
      },
    })
  }

  #playStep(index) {
    if (index === undefined) index = this.currentIndex

    const dropdown = this.dropdowns[index]
    const icon = dropdown.querySelector('[data-step-accordion-icon]')
    const svg = dropdown.querySelector('[data-step-accordion-svg]')
    const line = dropdown.querySelector('[data-step-accordion-line]')
    const clip = dropdown.querySelector('[data-step-accordion-clip]')

    const tl = gsap.timeline({ defaults: { ease: this.ease, duration: this.easeDuration } })
    tl.to(icon, { backgroundColor: 'var(--_color---secondary--meadow-green)' })

      .to(svg, { rotationZ: -180 }, '<')
      .to(line, { scaleX: 1 }, '<')
      .to(clip, { height: 'auto' }, '<')
      .to(this.visuals, { autoAlpha: 0 }, '<')
      .to(this.visuals[index], { autoAlpha: 1 }, '<')

    this.#startAutoplay(index)
  }

  #closeStep(index) {
    const dropdown = this.dropdowns[index]
    const icon = dropdown.querySelector('[data-step-accordion-icon]')
    const svg = dropdown.querySelector('[data-step-accordion-svg]')
    const line = dropdown.querySelector('[data-step-accordion-line]')
    const clip = dropdown.querySelector('[data-step-accordion-clip]')

    const tl = gsap.timeline({ defaults: { ease: this.ease, duration: this.easeDuration } })
    tl.to(icon, { backgroundColor: 'var(--_color---secondary--soft-green)' })

      .to(svg, { rotationZ: -0 }, '<')
      .to(line, { scaleX: 0 }, '<')
      .to(clip, { height: 0 }, '<')
  }

  #handleClick() {
    this.dropdowns.forEach((dropdown, index) => {
      const btn = dropdown.querySelector('[data-step-accordion-btn]')
      btn.addEventListener('click', () => {
        if (this.currentIndex === index) return

        this.#closeStep(this.currentIndex)
        gsap.killTweensOf(this.progresslines)

        gsap.set(this.progresslines, { scaleX: 0 })

        this.progresslines.forEach((line, index2) => {
          if (index > index2) {
            gsap.set(line, { scaleX: 1 })
          }
        })
        this.currentIndex = index

        this.#playStep(index)
      })
    })
  }
}

export default function (element, dataset = {}) {
  onView(element, {
    autoStart: true,
    once: true,
    rootMargin: '200px',
    callback: ({ isIn }) => {
      if (isIn) {
        const accordion = new StepAccordion(element, { duration: 10 })
        console.log('in')
      }
    },
  })

  const container = element.closest('.container')

  // --- SVG line draw ---
  const prepared = prepLineDraw(container)

  if (prepared) {
    let drawn = false

    onView(container, {
      threshold: 0.25,
      autoStart: true,
      callback: ({ isIn }) => {
        if (isIn && !drawn) {
          drawn = true
          playLineDraw(prepared)
        }
      },
    })

    onDestroy(() => {
      cleanLineDraw(prepared.path)
    })
  }
}
